import { useEffect, useRef, useState } from "react";
import { Button } from "./Button";

interface Props {
  open: boolean;
  title: string;
  body?: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  type?: "text" | "email";
  confirmLabel: string;
  cancelLabel?: string;
  loading?: boolean;
  error?: string | null;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  open, title, body, label, initialValue = "", placeholder, type = "text",
  confirmLabel, cancelLabel = "Cancel", loading = false, error, onConfirm, onCancel,
}: Props) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !loading) onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4"
      onClick={(e) => e.target === e.currentTarget && !loading && onCancel()}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); onConfirm(value.trim()); }}
        className="w-full max-w-md bg-white rounded-card border border-fh-border shadow-card p-6"
      >
        <h2 id="prompt-title" className="font-serif text-[20px] text-fh-ink mb-2">{title}</h2>
        {body && <p className="text-[14px] text-fh-gray leading-relaxed mb-4">{body}</p>}
        <label className="block text-[13px] font-semibold text-fh-green-dark mb-1.5">{label}</label>
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-[10px] border border-fh-border bg-white px-3.5 py-3 text-[15px] text-fh-ink placeholder:text-fh-gray-light focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30"
        />
        {error && <p className="text-[13px] text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button type="submit" size="sm" loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}
